import ValidationState from "../models/enums/validation-state.ts";
import IndicatorDefinition from "../models/enums/indicator-definition.ts";
import { setUrl, setUrlWithParams } from "./endpoints/endpoint-helper.ts";
import FlowMode from "@/models/enums/flow-mode.ts";
import DetectionProvider from "@/models/enums/plan-detection-provider.ts";

const ENDPOINTS = {
    LOGIN: setUrl("/account/Login"),
    LOGIN_JWT: setUrl("/account/LoginJwt"),
    LOGIN_COOKIE: setUrl("/account/LoginCookie"),

    BOOMER_BAAMER_REVISION_POSTED: (baamerRevisionId: string) =>
        setUrl(`/boomer/baamerRevision/${baamerRevisionId}/posted`),
    BOOMER_PROJECT_CAN_POST: (projectId: string) =>
        setUrl(`/boomer/project/${projectId}/canPost`),
    BOOMER_LAUNCH_POST_REVISION: (projectId: string) =>
        setUrl(`/boomer/baamerRevision/${projectId}/launch`),
    BOOMER_GET_LOCALISATION_INDICATORS: (localisationId: number) =>
        setUrl(`/boomer/ipc/localisation/${localisationId}`),
    BOOMER_GET_POST_REVISION_STATUS: (jobId: string) =>
        setUrl(`/boomer/baamerRevision/${jobId}/status`),

    FILE_REVISION: (baamerRevisionId: string) =>
        setUrl(`/fileRevisions/baamerRevision/${baamerRevisionId}`),

    FILE_REVISION_BY_PROJECT_ID: (projectId: string) =>
        setUrl(`/baamerFileRevision/project/${projectId}`),

    PACK_FILTER_NODE: (packId: string) => setUrl(`viewer/${packId}/filterNode`),

    GET_PROJECT: (projectId: string) => setUrl(`/projects/${projectId}`),
    PROJECT_CREATE: () => setUrl('/projects'),
    PROJECT_DESCRIPTIONS: (projectId: string) => setUrl(`/descriptions/project/${projectId}`),
    PROJECT_ACTIVE_DESCRIPTION: (projectId: string) => setUrl(`/descriptions/project/${projectId}/active`),
    PROJECTS: setUrl("/projects"),
    PROJECT_UPDATE_FLOW_MODE: (projectId: string, flowMode: FlowMode) => setUrl(`/projects/${projectId}/updateFlowMode/${flowMode}`),

    PROJECT_INDICATORS: (projectId: string, indicators: string[]) => setUrlWithParams(`/indicators/project/${projectId}`, { keyNames: indicators }),
    PROJECT_SYNC_STATUS: (projectId: string) => setUrl(`projects/${projectId}/syncStatus`),
    PROJECT_LOCK_STATUS: (projectId: string) => setUrl(`/projects/${projectId}/lockStatus`),
    PROJECT_LOCK_ADD: (projectId: string) => setUrl(`/projects/${projectId}/lock`),
    PROJECT_LOCK_REMOVE: (projectId: string) => setUrl(`/projects/${projectId}/unlock`),

    PROJECT_DOWNLOAD: (projectId: string) => setUrl(`/projects/${projectId}/download`),

    PROJECT_3D_FILES: (projectId: string, storeyName: string) => setUrl(`/viewer/${projectId}/3DfilePaths?storeyName=${encodeURIComponent(storeyName)}`),
    PROJECT_3D_BOOMER_FILES: (projectId: string) => setUrl(`/viewer/boomer/${projectId}/3DfilePaths`),
    PROJECT_MEASURE_3D_FILES: (projectId: string, baamerRevisionId: string, buildingId?: string, storeyId?: string) => {
        const params: Record<string, string> = {
            baamerRevisionId
        };

        if (buildingId) {
            params.buildingId = buildingId;
        }

        if (storeyId) {
            params.storeyId = storeyId;
        }

        return setUrlWithParams(`/Viewer/${projectId}/measure3DfilePaths`, params)
    },

    PACK: (baamerRevisionId: string) =>
        setUrl(`/packs/baamerRevision/${baamerRevisionId}`),
    PACKS: setUrl("/packs?pageSize=10000&pageNumber=1"),
    PROJECT_PACKS: (projectId: string) => setUrl(`/packs/project/${projectId}`),
    PACK_IMAGE: (
        packId: string,
        type: "ScopeStoreyByTgm" | "ObjetsDeLancement" | "Verticaux" | "GrosOeuvre" | "Enveloppes" | "Coques" | "TypesDeCoque",
    ) =>
        !!packId && !!type
            ? setUrl(`/packs/${packId}/surfaceImage/${type}`)
            : "",

    SHELL_VALIDATION_GET_BY_VALIDATION: (
        validationId: string
    ) => setUrl(`/shellValidations/validation/${validationId}`),
    SHELL_VALIDATION_SET_NEW_TYPES: setUrl('/shellValidations/types'),

    PACK_KEYNAME_INDICATORS: (
        storeyId: string,
        keyFrame: IndicatorDefinition
    ) => setUrl(`/indicators/pack/${storeyId}/keyName/${keyFrame}`),

    VALIDATION_SET_NEW_STATE: (
        validationId: string,
        newState: ValidationState,
        forceState: boolean
    ) => setUrl(`/validations/${validationId}/state/${newState}?forceState=${forceState}`),
    VALIDATIONS: setUrl("/validations?pageSize=10000&pageNumber=1"),

    VIEWER_FILE: (filePath: string) =>
        setUrl(`viewer/getFile?link=${encodeURIComponent(filePath)}`),
    VIEWER_FILE_SAAS: (filePath: string) =>
        setUrl(`viewer/getFileSaaS?link=${encodeURIComponent(filePath)}`),
    VIEWER_FILE_BOOMER: (filePath: string) =>
        setUrl(`viewer/getFileBoomer?link=${filePath}`), // La réponse donnée par l'API est déjà encodé. Réencoder fait un bug
    VIEWER_PDF_PREVIEW: (projectId: string, filePath: string, transparent = false, convert = true) =>
        setUrl(`viewer/${projectId}/getPdfPreview?link=${encodeURIComponent(filePath)}&transparent=${transparent}&convert=${convert}`), // La réponse donnée par l'API est déjà encodé. Réencoder fait un bug
    VIEWER_IFC_FILES_SAAS: (projectId: string) =>
        setUrl(`viewer/${projectId}/getIfcFilesSaaS`),

    PRODUCTION_LAUNCH_PROCESS: (
        projectId: string,
        processName: string,
        deleteExistingMeasures: boolean
    ) => setUrl(`/production/project/${projectId}/launch?processName=${encodeURIComponent(processName)}&deleteExistingMeasures=${deleteExistingMeasures}`),
    PRODUCTION_GET_JOB_STATUS: (
        jobId: string,
    ) => setUrl(`/production/job/${jobId}/status`),
    PRODUCTION_GET_PREVIOUSLY_LAUNCHED: (
        projectId: string,
    ) => setUrl(`/production/project/${projectId}/previouslyLaunched`),

    // FAKE_PRODUCTION_LAUNCH_PROCESS: (
    //     projectId: string,
    //     processName: string,
    // ) => setUrl(`/production/fake-launch/${projectId}?processName=${encodeURI(processName)}`),
    // FAKE_PRODUCTION_GET_JOB_STATUS: (
    //     jobId: string,
    // ) => setUrl(`/production/fake-get/${jobId}`),
    // FAKE_PRODUCTION_GET_JOB_DATA: (
    //     jobId: string,
    // ) => setUrl(`/production/fake-get/${jobId}/data`),

    REVISION_LAUNCH_NEW: (
        projectId: string,
    ) => setUrl(`/revisions/project/${projectId}/launchNew`),
    REVISION_GET_NEW_REVISION_STATUS: (
        jobId: string,
    ) => setUrl(`/revisions/launchNew/${jobId}/status`),

    FILTERS: setUrl("/filters/localisations"),
    MAP_DATA: (
        localisationId: number
    ) => setUrl(`filters/projectMapData/${localisationId}`),

    STOREY_TYPES: (setUrl('/storeyTypes')),
    SURFACE_PROCESSED_MAP_DATA: (
        localisationId: number,
        startDate: string,
        endDate: string,
        indicatorKeyName: string
    ) => setUrl(`filters/surfaceProcessedMapData/${localisationId}?startDate=${startDate}&endDate=${endDate}&indicatorKeyName=${indicatorKeyName}`),
    SURFACE_PROCESSED_PER_PHASE: (
        localisationId: number,
        startDate: string,
        endDate: string,
        indicatorKeyName: string
    ) => setUrl(`filters/surfaceProcessedPerPhase/${localisationId}?startDate=${startDate}&endDate=${endDate}&indicatorKeyName=${indicatorKeyName}`),
    SOURCE_REVISION_PER_PHASE: (
        localisationId: number,
        startDate: string,
        endDate: string
    ) => setUrl(`filters/sourceRevisionPerPhase/${localisationId}?startDate=${startDate}&endDate=${endDate}`),

    REVISION_BIMSYNC: (
        localisationId: number,
        startDate: string,
        endDate: string
    ) => setUrl(`filters/revisionBimSync/${localisationId}?startDate=${startDate}&endDate=${endDate}`),

    // WISEBIM_LAUNCH_TASK: (
    //     projectId: string,
    //     storeyId: string,
    //     fileRevisionId: string
    // ) => setUrl(`wisebim/project/${projectId}/storey/${storeyId}/fileRevision/${fileRevisionId}/launch`),
    // WISEBIM_STATUS_TASK: (
    //     taskId: string,
    //     fileName: string
    // ) => setUrl(`wisebim/task/${taskId}/status?fileName=${encodeURIComponent(fileName)}`),
    // WISEBIM_RESULT_TASK: (
    //     projectId: string,
    //     taskId: string,
    //     fileName: string
    // ) => setUrl(`wisebim/project/${projectId}/task/${taskId}/result?fileName=${encodeURIComponent(fileName)}`),

    PLAN_DETECTION_CONFIGURATION: (projectId: string) => setUrl(`projects/${projectId}/configuration/plan-detection`),
    PLAN_DETECTION_LAUNCH: (
        projectId: string,
        storeyId: string,
        fileRevisionId: string,
        detectionProvider: DetectionProvider
    ) => setUrl(`planDetection/project/${projectId}/storey/${storeyId}/fileRevision/${fileRevisionId}/provider/${detectionProvider}`),
    PLAN_DETECTION_STATUS: (
        projectId: string,
        taskId: string
    ) => setUrl(`planDetection/project/${projectId}/task/${taskId}`),
    PLAN_DETECTION_RESULT: (
        projectId: string,
        taskId: string
    ) => setUrl(`planDetection/project/${projectId}/task/${taskId}/result`),

    DASHBOARD_PROCESS_STATUS: (
        projectId: string
    ) => setUrl(`dashboardProcess/project/${projectId}`),
    DASHBOARD_UPDATE_PROCESS_STATUS: (
        projectId: string
    ) => setUrl(`dashboardProcess/project/${projectId}`),
    DASHBOARD_INITIAL_PROCESS_STATUS: (
        projectId: string
    ) => setUrl(`dashboardProcess/project/${projectId}/initialStatus`)
};

export const INDICATORS_ENDPOINTS = {
    // GET_PROJECT_INDICATORS_DEFINITIONS: (projectId: string, indicatorsKeyName: string[]) => setUrlWithParams(`boomer/project/${projectId}`, {keyNames: indicatorsKeyName}),
    GET_SURFACE_INDICATORS_DEF_METAOBJECTS: (projectId: string, storeyId: string, indicatorsKeyName: string[]) => setUrlWithParams(`boomer/surfaceIndicatorDefMetaObjects/project/${projectId}/storey/${storeyId}`, { keyNames: indicatorsKeyName }),
    GET_PROJECT_DEFINITIONS: (projectId: string, indicatorsKeyName: string[]) => setUrlWithParams(`/boomer/indicators/project/${projectId}/definitions`, { keyNames: indicatorsKeyName }),
    GET_STOREY_SURFACE_FILES: (projectId: string, storeyId: string, indicatorsKeyName: string[]) => setUrlWithParams(`/boomer/indicators/project/${projectId}/storey/${storeyId}`, { keyNames: indicatorsKeyName }),
}

export const PROJECT_REFERENCE_ENDPOINTS = {
    GET: (projectId: string) => setUrl(`/projectReference/project/${projectId}`),
    SAVE: (projectId: string) => setUrl(`/projectReference/project/${projectId}`),
    UPDATE_STOREY_FILE_DATA: (storeyId: string, baamerFileRevisionId: string) => setUrl(`/projectReference/storey/${storeyId}/baamerFileRevision/${baamerFileRevisionId}`),
}

export const GEOMETRIES_ENDPOINTS = {
    UPDATE_PROJECT_WALLS: (projectId: string) => setUrl(`/geometries/updateStoreyWalls/project/${projectId}`),
    UPDATE_PROJECT_OPENINGS: (projectId: string) => setUrl(`/geometries/updateStoreyOpenings/project/${projectId}`),
    UPDATE_PROJECT_SPACES: (projectId: string) => setUrl(`/geometries/updateStoreySpaces/project/${projectId}`),
    UPDATE_PROJECT_SLABS: (projectId: string) => setUrl(`/geometries/updateStoreySlabs/project/${projectId}`)
}

export const ORIGIN_ENDPOINTS = {
    UPDATE: (baamerFileRevisionId: string) => setUrl(`/origin/baamerFileRevision/${baamerFileRevisionId}`)
}

export const BAAMER_FILE_REVISION = {
    UPDATE_ORIGIN_DATA: (baamerFileRevisionId: string) => setUrl(`/baamerFileRevision/updateOriginData/${baamerFileRevisionId}`),
    UPDATE_ORIGINS_DATA: (baamerRevisionId: string) => setUrl(`/baamerFileRevision/updateOriginsData/baamerRevision/${baamerRevisionId}`)
}

export default ENDPOINTS;
